// V2 ticket placement engine. A donor ticket line maps to ONE single
// coupon on HRW's book: each submission mints one UUID, lines place
// sequentially with clientRef `hrw-v2:{uuid}:{index}`, and replaying a
// clientRef returns the ORIGINAL coupon (booking-service idempotency).
// The donor's `status-unknown` → "Check status" affordance therefore
// re-POSTs the same refs and converges truthfully — money can never be
// duplicated by a retry, and partial failures report the accepted subset.

import { ApiError } from "@/api";
import {
  type CouponReceipt,
  type CouponRequest,
  couponReconciling,
} from "@/betslip/controller";
import { presentBetSlipError } from "@/betslip/presentation";

import type { ToteLine } from "./hrwTote";

export type PlaceFn = (
  request: CouponRequest,
  clientRef: string,
) => Promise<CouponReceipt>;

export type PlacedLine = ToteLine & {
  clientRef: string;
  couponId: string | null;
  status: string;
  walletState: string;
  returnCents: number | null;
  reconciling: boolean;
};

export type TicketOutcome = {
  transaction: "accepted" | "rejected" | "status-unknown";
  accepted: PlacedLine[];
  /** Lines that were definitively refused, with the presented reason. */
  rejected: Array<{ line: ToteLine; code: string; message: string }>;
  /** Lines whose fate is unknown (network/backend ambiguity). */
  unknown: ToteLine[];
  message: string;
};

export function clientRefFor(submissionId: string, index: number): string {
  return `hrw-v2:${submissionId}:${index}`;
}

export function lineRequest(raceId: string, line: ToteLine): CouponRequest {
  return {
    type: "single",
    stakeCents: line.amount,
    legs: [{ raceId, betType: line.type, sel: line.sel }],
  };
}

function placedLine(
  line: ToteLine,
  clientRef: string,
  receipt: CouponReceipt,
): PlacedLine {
  return {
    ...line,
    clientRef,
    couponId: receipt.couponId,
    status: receipt.status,
    walletState: receipt.walletState,
    returnCents: receipt.returnedCents,
    reconciling: couponReconciling(receipt),
  };
}

/** 4xx (bar rate limiting) is a definitive pre-acceptance refusal: no coupon
 * exists and no money moved. Everything else is ambiguous and must go
 * through the idempotent replay before the ticket can claim an outcome. */
export function isDefinitiveRejection(error: unknown): error is ApiError {
  return (
    error instanceof ApiError &&
    error.status >= 400 &&
    error.status < 500 &&
    error.status !== 429
  );
}

function outcomeMessage(outcome: Omit<TicketOutcome, "message">): string {
  const placed = outcome.accepted.length;
  const refused = outcome.rejected.length;
  const unknown = outcome.unknown.length;
  if (outcome.transaction === "status-unknown") {
    return placed
      ? `${placed} bet${placed === 1 ? "" : "s"} accepted · ${unknown} awaiting confirmation`
      : "We could not confirm your bets. Check status to reconcile.";
  }
  if (outcome.transaction === "rejected") {
    return outcome.rejected[0]?.message ?? "This bet could not be accepted.";
  }
  if (refused || unknown) {
    const reason = outcome.rejected[0]?.message;
    return `${placed} of ${placed + refused + unknown} bets accepted${reason ? ` · ${reason}` : ""}`;
  }
  return "";
}

/**
 * Place every draft line sequentially. Stops on the first definitive
 * rejection (remaining lines are reported rejected with the same reason —
 * the player re-drafts deliberately rather than half a wheel landing after
 * an error) and on the first ambiguous failure (remaining lines stay
 * unknown until the replay).
 */
export async function placeTicket({
  raceId,
  lines,
  submissionId,
  place,
}: {
  raceId: string;
  lines: ToteLine[];
  submissionId: string;
  place: PlaceFn;
}): Promise<TicketOutcome> {
  const accepted: PlacedLine[] = [];
  const rejected: TicketOutcome["rejected"] = [];
  const unknown: ToteLine[] = [];

  for (const [index, line] of lines.entries()) {
    const clientRef = clientRefFor(submissionId, index);
    if (rejected.length || unknown.length) {
      // A stop already happened: never place trailing lines out of order.
      if (rejected.length) {
        rejected.push({
          line,
          code: rejected[0].code,
          message: rejected[0].message,
        });
      } else {
        unknown.push(line);
      }
      continue;
    }
    try {
      const receipt = await place(lineRequest(raceId, line), clientRef);
      accepted.push(placedLine(line, clientRef, receipt));
    } catch (error) {
      if (isDefinitiveRejection(error)) {
        const view = presentBetSlipError(error, "place");
        rejected.push({ line, code: view.code, message: view.message });
      } else {
        unknown.push(line);
      }
    }
  }

  const transaction: TicketOutcome["transaction"] = unknown.length
    ? "status-unknown"
    : accepted.length
      ? "accepted"
      : "rejected";
  const outcome = { transaction, accepted, rejected, unknown };
  return { ...outcome, message: outcomeMessage(outcome) };
}

/**
 * Resolve an ambiguous submission by replaying every clientRef. A ref whose
 * coupon exists returns the original (idempotent replay); a ref that never
 * reached the book either places now (the player's explicit intent — the
 * betting window still gates it server-side) or fails definitively with no
 * money at risk.
 */
export async function checkTicketStatus({
  raceId,
  lines,
  submissionId,
  place,
}: {
  raceId: string;
  lines: ToteLine[];
  submissionId: string;
  place: PlaceFn;
}): Promise<TicketOutcome> {
  const accepted: PlacedLine[] = [];
  const rejected: TicketOutcome["rejected"] = [];
  const unknown: ToteLine[] = [];

  for (const [index, line] of lines.entries()) {
    const clientRef = clientRefFor(submissionId, index);
    try {
      const receipt = await place(lineRequest(raceId, line), clientRef);
      accepted.push(placedLine(line, clientRef, receipt));
    } catch (error) {
      if (isDefinitiveRejection(error)) {
        const view = presentBetSlipError(error, "place");
        rejected.push({ line, code: view.code, message: view.message });
      } else {
        unknown.push(line);
      }
    }
  }

  const transaction: TicketOutcome["transaction"] = unknown.length
    ? "status-unknown"
    : accepted.length
      ? "accepted"
      : "rejected";
  const outcome = { transaction, accepted, rejected, unknown };
  return { ...outcome, message: outcomeMessage(outcome) };
}

// ---------------------------------------------------------------------
// Account-scoped pending-submission recovery (the Quick Rip lesson): a
// pending ticket persists per PLAYER, is never auto-replayed, and another
// account's record is discarded — replaying player A's refs under player
// B's session would stake B's money on A's intent.

const PENDING_PREFIX = "hw.v2-ticket.pending.v1";

export type PendingTicket = {
  submissionId: string;
  raceId: string;
  lines: ToteLine[];
  createdAt: string;
};

function pendingKey(playerId: number): string {
  return `${PENDING_PREFIX}.${playerId}`;
}

export function clearForeignPendingTickets(playerId: number): void {
  try {
    const keep = pendingKey(playerId);
    const drop: string[] = [];
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);
      if (key && key.startsWith(PENDING_PREFIX) && key !== keep) {
        drop.push(key);
      }
    }
    for (const key of drop) window.sessionStorage.removeItem(key);
  } catch {
    // Sandboxed storage: recovery is best-effort only.
  }
}

export function loadPendingTicket(playerId: number): PendingTicket | null {
  try {
    const raw = window.sessionStorage.getItem(pendingKey(playerId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingTicket;
    if (
      typeof parsed.submissionId !== "string" ||
      typeof parsed.raceId !== "string" ||
      !Array.isArray(parsed.lines)
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function savePendingTicket(
  playerId: number,
  ticket: PendingTicket,
): void {
  try {
    window.sessionStorage.setItem(pendingKey(playerId), JSON.stringify(ticket));
  } catch {
    // Sandboxed storage: recovery is best-effort only.
  }
}

export function clearPendingTicket(playerId: number): void {
  try {
    window.sessionStorage.removeItem(pendingKey(playerId));
  } catch {
    // Sandboxed storage: recovery is best-effort only.
  }
}
